import { Component, OnInit } from '@angular/core';
import { DynamicDialogRef, DialogService, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { ToastrService } from 'ngx-toastr';
import { firstValueFrom } from 'rxjs';
import { Program } from '@models';
import { DateFns, UtilFns } from '@utils';

export interface IProgramChangeEndDateParams {
  program: Program;
}

/**
 * Moves a program's end date without the Hold / edit / save / re-Activate ritual, which is the
 * step admins reliably forget and which leaves the program silently blocking orders.
 *
 * Only offered for programs that HAVE an explicit end date - Fixed Start and End Date, or
 * Variable Start Date with Fixed End Date. A Variable Start and End Date program derives its end
 * from each user's join date, so there is no single date to move.
 *
 * Two guardrails, both enforced on the picker AND re-checked on Proceed, because the picker only
 * stops a click and says nothing about a typed value:
 *   - the new date must be strictly after today, so the change can never expire the program
 *   - the new date may be at most 12 months from today
 */
@Component({
  selector: 'prox-program-change-end-date-dialog',
  standalone: false,
  templateUrl: './program-change-end-date.dialog.component.html',
})
export class ProgramChangeEndDateDialogComponent implements OnInit {
  program!: Program;
  newEndDate?: Date;

  /** Midnight today. Comparisons are made at day granularity - a time-of-day difference would
   *  make "the same day" read as later or earlier depending on when the page was opened. */
  today = DateFns.toZeroDate(DateFns.today());
  minDate = DateFns.dateAdd(DateFns.toZeroDate(DateFns.today()), 1, 'days');
  maxDate = DateFns.dateAdd(DateFns.toZeroDate(DateFns.today()), 12, 'months');

  constructor(
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig<IProgramChangeEndDateParams>,
    private toastr: ToastrService,
  ) {}

  async ngOnInit() {
    const params = this.config.data;
    UtilFns.assertNonNull(params);
    this.program = params.program;
    // Seed with the current end date only when it is still a legal choice, so the picker never
    // opens showing a value that Proceed would reject.
    const current = this.program.endDate ? DateFns.toZeroDate(this.program.endDate) : undefined;
    if (current != null && current > this.today && current <= this.maxDate) {
      this.newEndDate = current;
    }
  }

  public static async openDialog(
    dialogService: DialogService,
    data: IProgramChangeEndDateParams,
    config?: DynamicDialogConfig,
  ) {
    const ref = dialogService.open(ProgramChangeEndDateDialogComponent, {
      closable: false,
      draggable: true,
      width: '38rem',
      maximizable: false,
      resizable: true,
      modal: true,
      focusOnShow: false,
      ...config,
      data: data,
    });
    const r = await firstValueFrom(ref.onClose);
    return r as Date | null;
  }

  onProceed() {
    if (!this.newEndDate) {
      this.toastr.warning('Please choose a new end date', 'Cannot Proceed');
      return;
    }

    const picked = DateFns.toZeroDate(this.newEndDate);

    if (picked <= this.today) {
      this.toastr.warning('The new end date must be later than today', 'Cannot Proceed');
      return;
    }

    if (picked > this.maxDate) {
      this.toastr.warning('The new end date cannot be more than 12 months from today', 'Cannot Proceed');
      return;
    }

    this.ref.close(picked);
  }

  onCancel() {
    this.ref.close(null);
  }
}
